/**
 * Opengater small catalog hooks (users service): device-options,
 * user-info-triggers (both CRUD), flow-ttl-settings (list + per-flow PUT).
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiOg } from './client';
// ─── device-options ────────────────────────────────────────────────────────
const DO_PATH = '/api/v1/admin/device-options';
const DO_KEY = ['og', 'device-options'];
export function useOgDeviceOptions() {
    return useQuery({ queryKey: DO_KEY, queryFn: () => apiOg('users', DO_PATH) });
}
export function useCreateOgDeviceOption() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('users', DO_PATH, { method: 'POST', body }),
        onSuccess: () => qc.invalidateQueries({ queryKey: DO_KEY }),
    });
}
export function useUpdateOgDeviceOption() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => apiOg('users', `${DO_PATH}/${id}`, { method: 'PATCH', body: patch }),
        onSuccess: () => qc.invalidateQueries({ queryKey: DO_KEY }),
    });
}
export function useDeleteOgDeviceOption() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiOg('users', `${DO_PATH}/${id}`, { method: 'DELETE' }),
        onSuccess: () => qc.invalidateQueries({ queryKey: DO_KEY }),
    });
}
// ─── user-info-triggers ────────────────────────────────────────────────────
const UT_PATH = '/api/v1/admin/user-info-triggers';
const UT_KEY = ['og', 'user-info-triggers'];
export function useOgUserTriggers() {
    return useQuery({ queryKey: UT_KEY, queryFn: () => apiOg('users', UT_PATH) });
}
export function useCreateOgUserTrigger() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('users', UT_PATH, { method: 'POST', body }),
        onSuccess: () => qc.invalidateQueries({ queryKey: UT_KEY }),
    });
}
export function useUpdateOgUserTrigger() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => apiOg('users', `${UT_PATH}/${id}`, { method: 'PATCH', body: patch }),
        onSuccess: () => qc.invalidateQueries({ queryKey: UT_KEY }),
    });
}
export function useDeleteOgUserTrigger() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiOg('users', `${UT_PATH}/${id}`, { method: 'DELETE' }),
        onSuccess: () => qc.invalidateQueries({ queryKey: UT_KEY }),
    });
}
// ─── flow-ttl-settings (list + per-flow PUT, no add/delete) ─────────────────
const FT_PATH = '/api/v1/admin/flow-ttl-settings';
const FT_KEY = ['og', 'flow-ttl'];
export function useOgFlowTtl() {
    return useQuery({ queryKey: FT_KEY, queryFn: () => apiOg('users', FT_PATH) });
}
export function useUpdateOgFlowTtl(flow) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch) => apiOg('users', `${FT_PATH}/${encodeURIComponent(flow)}`, { method: 'PUT', body: patch }),
        onSuccess: () => qc.invalidateQueries({ queryKey: FT_KEY }),
    });
}
// ─── currencies (list + per-code PATCH) ─────────────────────────────────────
const CUR_PATH = '/api/v1/admin/currencies';
const CUR_KEY = ['og', 'currencies'];
export function useOgCurrencies() {
    return useQuery({ queryKey: CUR_KEY, queryFn: () => apiOg('users', CUR_PATH) });
}
export function useUpdateOgCurrency(code) {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (patch) => apiOg('users', `${CUR_PATH}/${encodeURIComponent(code)}`, { method: 'PATCH', body: patch }),
        onSuccess: () => qc.invalidateQueries({ queryKey: CUR_KEY }),
    });
}
// ─── payment-periods (CRUD) ─────────────────────────────────────────────────
const PP_PATH = '/api/v1/admin/payment-periods';
const PP_KEY = ['og', 'payment-periods'];
export function useOgPaymentPeriods() {
    return useQuery({ queryKey: PP_KEY, queryFn: () => apiOg('users', PP_PATH) });
}
export function useCreateOgPaymentPeriod() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (body) => apiOg('users', PP_PATH, { method: 'POST', body }),
        onSuccess: () => qc.invalidateQueries({ queryKey: PP_KEY }),
    });
}
export function useUpdateOgPaymentPeriod() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: ({ id, patch }) => apiOg('users', `${PP_PATH}/${id}`, { method: 'PATCH', body: patch }),
        onSuccess: () => qc.invalidateQueries({ queryKey: PP_KEY }),
    });
}
export function useDeleteOgPaymentPeriod() {
    const qc = useQueryClient();
    return useMutation({
        mutationFn: (id) => apiOg('users', `${PP_PATH}/${id}`, { method: 'DELETE' }),
        onSuccess: () => qc.invalidateQueries({ queryKey: PP_KEY }),
    });
}
